import { usePageState } from "@/navigation/NavigationProvider";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import BoardWorkspace from "@/components/BoardWorkspace";
import { toast } from "sonner";
import { CommunicationWorkspaceRows } from "@/components/CommunicationWorkspaceRows";
import { DesktopCommunicationDetailPanel } from "@/components/DesktopCommunicationDetailPanel";
import { MobileCommunicationDrawer } from "@/components/MobileCommunicationDrawer";
import { useAuth } from "@/auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;
const visibilityFilters = [
  ["all", "All visibility"],
  ["company", "Company"],
  ["personal", "Personal"],
  ["selected_people", "Selected people"],
];
const authorFilters = [
  ["all", "All people"],
  ["mine", "My notes"],
  ["team", "Team notes"],
];

const initialForm = {
  note: "",
  additional_notes: "",
  project_id: "",
  visibility: "company",
  visible_to_user_ids: [],
};

export default function CommunicationLogWorkspacePage() {
  const { user, canManageCommunications, refresh } = useAuth();
  const location = useLocation();
  const linkedItemId = new URLSearchParams(location.search).get("item") || "";
  const handledLink = useRef("");
  const [loaded, setLoaded] = useState(false);
  const [linkError, setLinkError] = useState("");
  const isSupervisor = !canManageCommunications;
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [peopleSearch, setPeopleSearch] = useState("");
  const [projectSearch, setProjectSearch] = useState("");
  const [searchQuery, setSearchQuery] = usePageState("searchQuery", "");
  const [visibilityFilter, setVisibilityFilter] = usePageState("visibilityFilter", "all");
  const [authorFilter, setAuthorFilter] = usePageState("authorFilter", "all");
  const [showCompletedHistory, setShowCompletedHistory] = usePageState("showCompletedHistory", false);
  const [isCreating, setIsCreating] = useState(false);
  const [desktopItemId, setDesktopItemId] = useState("");
  const [mobileItemId, setMobileItemId] = useState("");

  const load = useCallback(async () => {
    const response = await axios.get(`${API}/communication-log`, {
      params: { filter: "workspace" },
    });
    setItems(response.data.items);
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded || !linkedItemId) return;
    const key = `${user?.id}:${linkedItemId}`;
    if (handledLink.current === key) return;
    handledLink.current = key;
    const item = items.find(row => row.id === linkedItemId);
    if (!item) {
      setLinkError("This communication is unavailable or no longer visible to you.");
      return;
    }
    setLinkError("");
    setSearchQuery("");
    setVisibilityFilter("all");
    setAuthorFilter("all");
    if (item.status === "complete" || item.deleted_at) setShowCompletedHistory(true);
    if (window.innerWidth < 768) setMobileItemId(item.id);
    else setDesktopItemId(item.id);
  }, [items, linkedItemId, loaded, user?.id, setSearchQuery, setVisibilityFilter, setAuthorFilter, setShowCompletedHistory]);

  const onWorkspaceLoadError = useCallback((error, fallback) => {
    const detail = error.response?.data?.detail;
    if (error.response?.status === 403 && typeof detail === "string"
        && detail.toLowerCase().includes("communication log module is disabled")) {
      toast.error(detail);
      refresh().catch(() => {});
      return;
    }
    toast.error(fallback);
  }, [refresh]);

  useEffect(() => {
    load().catch((error) => onWorkspaceLoadError(error, "Communication Log could not be loaded."));
    const refreshActiveLog = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      load().catch((error) => onWorkspaceLoadError(error, "Communication Log could not be refreshed."));
    };
    const refreshInterval = window.setInterval(refreshActiveLog, 30000);
    window.addEventListener("focus", refreshActiveLog);
    document.addEventListener("visibilitychange", refreshActiveLog);
    return () => {
      window.clearInterval(refreshInterval);
      window.removeEventListener("focus", refreshActiveLog);
      document.removeEventListener("visibilitychange", refreshActiveLog);
    };
  }, [load, onWorkspaceLoadError]);

  useEffect(() => {
    axios.get(`${API}/assignable-users`).then((response) => {
      setUsers(response.data.users);
    });
    axios.get(`${API}/bootstrap`).then((response) => {
      setProjects(response.data.projects || []);
    });
  }, []);

  const messageFor = (error, fallback) => {
    const detail = error.response?.data?.detail;
    if (error.response?.status === 403 && typeof detail === "string"
        && detail.toLowerCase().includes("communication log module is disabled")) {
      refresh().catch(() => {});
    }
    return typeof detail === "string" ? detail : fallback;
  };
  const matchingProjects = useMemo(() => {
    const normalizedSearch = projectSearch.toLowerCase();
    return projects.filter((project) => (
      `${project.project_code} ${project.name}`.toLowerCase().includes(normalizedSearch)
    ));
  }, [projectSearch, projects]);
  const matchingPeople = useMemo(() => {
    const normalizedSearch = peopleSearch.toLowerCase();
    return users.filter((person) => person.name.toLowerCase().includes(normalizedSearch));
  }, [peopleSearch, users]);

  const create = async (event) => {
    event.preventDefault();
    if (isCreating) {
      return;
    }
    const note = form.note.trim();
    if (!note) {
      toast.error("Note is required.");
      return;
    }
    // JFM-NOTE-004: Supervisors can only create project-attached field notes
    // through the field-notes endpoint. The /communications endpoint requires
    // communications:manage which Supervisors do not hold.
    if (isSupervisor) {
      if (!form.project_id) {
        toast.error("Choose a project — supervisors add project-linked notes only.");
        return;
      }
      setIsCreating(true);
      try {
        await axios.post(`${API}/projects/${form.project_id}/field-notes`, {
          notes: note,
        });
        setForm(initialForm);
        setPeopleSearch("");
        setProjectSearch("");
        await load();
        toast.success("Field note added to project.");
        window.dispatchEvent(new Event("jfm:badge-refresh"));
      } catch (error) {
        toast.error(messageFor(error, "Field note could not be added."));
      } finally {
        setIsCreating(false);
      }
      return;
    }
    if (
      form.visibility === "selected_people"
      && !form.visible_to_user_ids.length
    ) {
      toast.error("Select at least one person.");
      return;
    }
    setIsCreating(true);
    try {
      await axios.post(`${API}/communications`, {
        note,
        additional_notes: form.additional_notes.trim(),
        ...(form.project_id ? { project_id: form.project_id } : {}),
        visibility: form.visibility,
        ...(form.visibility === "selected_people"
          ? { visible_to_user_ids: form.visible_to_user_ids }
          : {}),
      });
      setForm(initialForm);
      setPeopleSearch("");
      setProjectSearch("");
      await load();
      toast.success("Open communication saved.");
      window.dispatchEvent(new Event("jfm:badge-refresh"));
    } catch (error) {
      toast.error(messageFor(error, "Item could not be saved."));
    } finally {
      setIsCreating(false);
    }
  };

  const resolve = async (itemId) => {
    try {
      const response = await axios.post(`${API}/communication-items/${itemId}/complete`);
      setItems((current) => current.map((item) => (
        item.id === itemId ? { ...item, ...response.data } : item
      )));
      toast.success("Communication moved to Completed History.");
      window.dispatchEvent(new Event("jfm:badge-refresh"));
      return true;
    } catch (error) {
      toast.error(messageFor(error, "Item could not be resolved."));
      return false;
    }
  };

  const convertToTask = async (itemId) => {
    try {
      const response = await axios.post(
        `${API}/communication-items/${itemId}/convert-to-task`,
        {},
      );
      setItems((current) => current.map((item) => (
        item.id === itemId ? { ...item, ...response.data } : item
      )));
      toast.success("Open communication moved to Tasks.");
      window.dispatchEvent(new Event("jfm:badge-refresh"));
      return true;
    } catch (error) {
      toast.error(messageFor(error, "Communication could not become a Task."));
      return false;
    }
  };

  const saveEdit = async (itemId, editForm) => {
    if (
      editForm.visibility === "selected_people"
      && !editForm.visible_to_user_ids.length
    ) {
      toast.error("Select at least one person.");
      return null;
    }
    try {
      const payload = {
        project_id: editForm.project_id,
        visibility: editForm.visibility,
        ...(editForm.visibility === "selected_people"
          ? { visible_to_user_ids: editForm.visible_to_user_ids }
          : {}),
      };
      if (editForm.original_note_changed) {
        payload.note = editForm.note.trim();
      }
      const response = await axios.patch(`${API}/communication-items/${itemId}`, payload);
      setItems((current) => current.map((item) => (
        item.id === itemId ? { ...item, ...response.data } : item
      )));
      toast.success("Communication updated.");
      window.dispatchEvent(new Event("jfm:badge-refresh"));
      return response.data;
    } catch (error) {
      toast.error(messageFor(error, "Communication could not be updated."));
      return null;
    }
  };

  const addAdditionalNote = async (itemId, message) => {
    try {
      const response = await axios.post(
        `${API}/communication-items/${itemId}/additional-notes`,
        { message },
      );
      setItems((current) => current.map((item) => (
        item.id === itemId ? { ...item, ...response.data } : item
      )));
      toast.success("Additional Note added.");
      return response.data;
    } catch (error) {
      toast.error(messageFor(error, "Additional Note could not be added."));
      return null;
    }
  };

  const updateAdditionalNote = async (itemId, additionalNoteId, message) => {
    try {
      const response = await axios.patch(
        `${API}/communication-items/${itemId}/additional-notes/${additionalNoteId}`,
        { message },
      );
      setItems((current) => current.map((item) => (
        item.id === itemId ? { ...item, ...response.data } : item
      )));
      toast.success("Additional Note updated.");
      return response.data;
    } catch (error) {
      toast.error(messageFor(error, "Additional Note could not be updated."));
      return null;
    }
  };

  const projectLabel = useCallback((projectId) => {
    const project = projects.find((candidate) => candidate.id === projectId);
    return project
      ? `${project.project_code} · ${project.name}`
      : "No project link";
  }, [projects]);

  const visibilityLabel = (item) => {
    if (item.visibility === "personal") {
      return "Personal · Only me";
    }
    if (item.visibility === "company") {
      return "Company · Everyone";
    }
    if (item.visibility === "selected_people") {
      const names = (item.visible_to_user_ids || []).map(
        (id) => users.find((user) => user.id === id)?.name || "Unavailable user",
      );
      return `Selected People · ${names.join(", ") || "Unavailable user"}`;
    }
    return "Shared";
  };

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const itemGroups = useMemo(() => {
    const filteredItems = items.filter((item) => {
      if (visibilityFilter !== "all" && item.visibility !== visibilityFilter) {
        return false;
      }
      if (authorFilter === "mine" && item.created_by_user_id !== user?.id) {
        return false;
      }
      if (authorFilter === "team" && item.created_by_user_id === user?.id) {
        return false;
      }
      if (!normalizedSearch) {
        return true;
      }
      const selectedPeople = (item.visible_to_user_ids || []).map(
        (id) => users.find((person) => person.id === id)?.name || "",
      );
      const searchableText = [
        item.notes,
        item.original_note,
        item.additional_notes,
        ...(item.additional_note_entries || []).map((entry) => entry.message),
        item.title,
        projectLabel(item.project_id),
        item.created_by_name_snapshot,
        ...selectedPeople,
      ]
        .join(" ")
        .toLowerCase();
      return searchableText.includes(normalizedSearch);
    });
    return {
      filteredItems,
    };
  }, [authorFilter, items, normalizedSearch, projectLabel, user?.id, users, visibilityFilter]);
  const selectedItems = useMemo(() => {
    return {
      desktopItem: items.find((item) => item.id === desktopItemId),
      mobileItem: items.find((item) => item.id === mobileItemId),
    };
  }, [desktopItemId, items, mobileItemId]);
  const { filteredItems } = itemGroups;
  const openItems = filteredItems.filter((item) => (
    !item.deleted_at && item.status !== "complete" && item.item_type === "note"
  ));
  const taskItems = filteredItems.filter((item) => (
    !item.deleted_at && item.status !== "complete" && item.item_type === "task"
  ));
  const completedHistory = filteredItems.filter((item) => (
    item.status === "complete" || Boolean(item.deleted_at)
  ));
  const { desktopItem, mobileItem } = selectedItems;

  return (
    <BoardWorkspace
      eyebrow="Shared coordination"
      headingTestId="communication-log-heading"
      subtitle={
        isSupervisor
          ? "Project-linked field notes visible to you."
          : "Human-created Tasks and Notes, with private and shared history."
      }
      title="Communication Log"
    >
      {linkError && <p role="alert">{linkError}</p>}
      <section
        className="project-notes-panel"
        data-testid="communication-log-workspace"
      >
        <form
          className="field-note-form communication-create-form"
          data-testid="communication-create-form"
          onSubmit={create}
        >
          <textarea
            autoFocus
            data-testid="communication-note-input"
            onChange={(event) => setForm({ ...form, note: event.target.value })}
            placeholder={
              isSupervisor
                ? "Write a field note for a project…"
                : "Write what needs to happen or what people need to know..."
            }
            required
            value={form.note}
          />
          {!isSupervisor && (
            <textarea
              data-testid="communication-additional-notes-input"
              onChange={(event) => setForm({ ...form, additional_notes: event.target.value })}
              placeholder="Additional Notes (optional)"
              value={form.additional_notes}
            />
          )}
          <input
            data-testid="communication-project-search-input"
            onChange={(event) => {
              setProjectSearch(event.target.value);
              if (!event.target.value) {
                setForm({ ...form, project_id: "" });
              }
            }}
            placeholder={
              isSupervisor
                ? "Choose a project (required)…"
                : "Search Project number or Project name..."
            }
            required={isSupervisor}
            value={projectSearch}
          />
          {projectSearch && !form.project_id && (
            <div data-testid="communication-project-options">
              {matchingProjects.map((project) => (
                  <button
                    data-testid={`communication-project-option-${project.id}`}
                    key={project.id}
                    onClick={() => {
                      setForm({ ...form, project_id: project.id });
                      setProjectSearch(`${project.project_code} — ${project.name}`);
                    }}
                    type="button"
                  >
                    {project.project_code} — {project.name}
                  </button>
                ))}
            </div>
          )}
          {form.project_id && (
            <button
              data-testid="communication-project-clear"
              onClick={() => {
                setForm({ ...form, project_id: "" });
                setProjectSearch("");
              }}
              type="button"
            >
              Clear Project
            </button>
          )}
          {!isSupervisor && (
            <div data-testid="communication-visibility-options">
              {["personal", "company", "selected_people"].map((visibility) => (
                <button
                  aria-pressed={form.visibility === visibility}
                  className={form.visibility === visibility ? "active" : ""}
                  data-testid={`communication-visibility-${visibility}`}
                  key={visibility}
                  onClick={() => setForm({
                    ...form,
                    visibility,
                    visible_to_user_ids: visibility === "selected_people"
                      ? form.visible_to_user_ids
                      : [],
                  })}
                  type="button"
                >
                  {visibility.replaceAll("_", " ")}
                </button>
              ))}
            </div>
          )}
          {!isSupervisor && form.visibility === "selected_people" && (
            <div data-testid="communication-selected-people-selector">
              <input
                data-testid="communication-people-search"
                onChange={(event) => setPeopleSearch(event.target.value)}
                placeholder="Search people..."
                value={peopleSearch}
              />
              {matchingPeople.map((person) => (
                  <label key={person.id}>
                    <input
                      checked={form.visible_to_user_ids.includes(person.id)}
                      data-testid={`communication-person-${person.id}`}
                      onChange={() => setForm({
                        ...form,
                        visible_to_user_ids: form.visible_to_user_ids.includes(person.id)
                          ? form.visible_to_user_ids.filter((id) => id !== person.id)
                          : [...form.visible_to_user_ids, person.id],
                      })}
                      type="checkbox"
                    />
                    {person.name} — {person.role}
                  </label>
                ))}
            </div>
          )}
          <button
            className="invoice-primary-button"
            data-testid="communication-create-submit"
            disabled={
              isCreating
              || (
              form.visibility === "selected_people"
              && !form.visible_to_user_ids.length
              )
            }
            type="submit"
          >
            {isCreating ? "Saving…" : "Save"}
          </button>
        </form>
        <div className="communication-log-toolbar" data-testid="communication-log-toolbar">
          <div className="communication-search-control">
            <input
              aria-label="Search communication"
              data-testid="communication-search-input"
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search notes, projects, or people..."
              value={searchQuery}
            />
            {searchQuery && (
              <button
                data-testid="communication-search-clear"
                onClick={() => setSearchQuery("")}
                type="button"
              >
                Clear
              </button>
            )}
          </div>
          <div
            aria-label="Filter by visibility"
            className="communication-filter-group"
            data-testid="communication-visibility-filters"
          >
            {visibilityFilters.map(([value, label]) => (
              <button
                aria-pressed={visibilityFilter === value}
                className={visibilityFilter === value ? "active" : ""}
                data-testid={`communication-visibility-filter-${value}`}
                key={value}
                onClick={() => setVisibilityFilter(value)}
                type="button"
              >
                {label}
              </button>
            ))}
          </div>
          <div
            aria-label="Filter by note creator"
            className="communication-filter-group"
            data-testid="communication-author-filters"
          >
            {authorFilters.map(([value, label]) => (
              <button
                aria-pressed={authorFilter === value}
                className={authorFilter === value ? "active" : ""}
                data-testid={`communication-author-filter-${value}`}
                key={value}
                onClick={() => setAuthorFilter(value)}
                type="button"
              >
                {label}
              </button>
            ))}
          </div>
          <p data-testid="communication-result-count">
            {filteredItems.length} {filteredItems.length === 1 ? "result" : "results"}
            {` · ${openItems.length} open · ${taskItems.length} tasks`}
            {` · ${completedHistory.length} completed history`}
          </p>
          <button
            aria-pressed={showCompletedHistory}
            data-testid="communication-completed-history-toggle"
            onClick={() => setShowCompletedHistory((current) => !current)}
            type="button"
          >
            {showCompletedHistory ? "Hide Completed History" : "Completed History"}
          </button>
        </div>
        <CommunicationWorkspaceRows
          canManageCommunications={canManageCommunications}
          completedHistory={completedHistory}
          onComplete={resolve}
          onConvertToTask={convertToTask}
          onOpenDesktop={setDesktopItemId}
          onOpenMobile={setMobileItemId}
          openItems={openItems}
          projectLabel={projectLabel}
          showCompletedHistory={showCompletedHistory}
          taskItems={taskItems}
          visibilityLabel={visibilityLabel}
        />
        {desktopItem && (
          <DesktopCommunicationDetailPanel
            canManageCommunications={canManageCommunications}
            item={desktopItem}
            onClose={() => setDesktopItemId("")}
            onAddAdditionalNote={addAdditionalNote}
            onComplete={resolve}
            onConvertToTask={convertToTask}
            onSave={saveEdit}
            onUpdateAdditionalNote={updateAdditionalNote}
            projectLabel={projectLabel}
            projects={projects}
            users={users}
            visibilityLabel={visibilityLabel}
          />
        )}
        {mobileItem && (
          <MobileCommunicationDrawer
            canManageCommunications={canManageCommunications}
            item={mobileItem}
            onClose={() => setMobileItemId("")}
            onAddAdditionalNote={addAdditionalNote}
            onComplete={resolve}
            onConvertToTask={convertToTask}
            onSave={saveEdit}
            onUpdateAdditionalNote={updateAdditionalNote}
            projectLabel={projectLabel}
            projects={projects}
            users={users}
            visibilityLabel={visibilityLabel}
          />
        )}
      </section>

    </BoardWorkspace>
  );
}

